// Recording engine constants shared across all apps that embed the recording engine.
// App-specific tunables (e.g. max minutes per app) live in the app's own config.

/** The common WebAudio rate (Hz). The recorder itself runs at the device's own rate. */
export const AUDIO_SAMPLE_RATE = 48000;

/**
 * Interim IndexedDB persist threshold (bytes).
 * Blobs are saved to IndexedDB every time the accumulator exceeds this size —
 * much lower than the S3 5 MB minimum so data is durably persisted frequently.
 */
export const IDB_PERSIST_THRESHOLD_BYTES = 512 * 1024; // 512 KB

/** Max retry attempts for a single S3 part upload */
export const UPLOAD_MAX_RETRIES = 3;

/** Exponential backoff delays (ms) between retry attempts: 1s, 2s, 4s */
export const UPLOAD_RETRY_DELAYS_MS = [1000, 2000, 4000] as const;

/** Default IndexedDB database name (apps may override via RecordingEngineConfig) */
export const DEFAULT_IDB_DB_NAME = 'clarix-recording';

/** Default IndexedDB object store name */
export const DEFAULT_IDB_STORE_NAME = 'upload_parts';

/** IndexedDB schema version */
export const IDB_DB_VERSION = 1;

/** The first local save comes sooner, so a take lost early still leaves audio on the device. */
export const IDB_FIRST_PERSIST_THRESHOLD_BYTES = 64 * 1024; // 64 KB

/** A live recorder that hands over no audio, or whose mic delivers nothing, for this long is stopped. */
export const RECORDER_STALL_MS = 15_000;

/** A watchdog tick later than this means the page itself was frozen (sleep), not the mic. */
export const RECORDER_TICK_GAP_MS = 5_000;

/** Long enough for a user-activated context to start; Safari can leave the promise pending. */
export const AUDIO_CONTEXT_RESUME_TIMEOUT_MS = 1_000;

/** An S3 part PUT with no upload progress for this long is aborted and retried. */
export const S3_PUT_IDLE_TIMEOUT_MS = 120_000;

export const RECORDER_STALLED_MESSAGE = 'No sound from your mic. We stopped and saved your recording.';
export const RECORDER_STALLED_EMPTY_MESSAGE = 'No sound from your mic, so we stopped the recording.';
export const RECORDER_ERROR_MESSAGE = 'The recording stopped unexpectedly. We saved what was recorded.';
export const RECORDER_ERROR_EMPTY_MESSAGE = 'The recording stopped unexpectedly.';
export const RECORDING_STOP_FAILED_MESSAGE = "Couldn't save the recording.";
export const RECORDING_UPLOADED_MESSAGE = 'Recording uploaded — AI is processing your meeting insights';
export const CHUNK_UPLOAD_FAILED_MESSAGE = "Some audio couldn't upload yet. We'll keep trying — keep this tab open.";
/** A stop or upload that failed only for want of a network: the audio waits on this device. */
export const NETWORK_MESSAGE = "Can't reach the network right now. The recording is kept on this device.";
/** S3 keeps not answering while the internet works: still retried, but no longer called offline. */
export const UPLOAD_UNREACHABLE_MESSAGE = "Couldn't upload the recording. It's kept on this device.";
/** The server did not take the recording; nothing on this device was cleared. */
export const SAVE_REFUSED_MESSAGE = "Couldn't save the recording. The audio is kept on this device.";

/** No-response S3 PUTs in a row, with the internet reachable, before the upload counts as unreachable. */
export const S3_UNREACHABLE_AFTER = 3;

/** A peak below this (about -100 dBFS) is digital silence: a real room, however quiet, sits near -60. */
export const DEAD_MIC_PEAK = 1e-5;

/** On a tab hide, unsaved audio is written to IndexedDB at most this often (each write uses a part number). */
export const HIDE_FLUSH_MIN_INTERVAL_MS = 60_000;
